import { caseApiRequest, workflowApiRequest } from './apiClient';
import type { ApiResult } from './types';
import { isCaseRequestUuid } from '../pages/task-case/taskCaseUpdateHelpers';

function workflowAppCode(): string {
  return (import.meta.env.VITE_APP_CODE_WORKFLOW ?? 'aa').trim();
}

function strField(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function numField(obj: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  }
  return 0;
}

function boolField(obj: Record<string, unknown>, ...keys: string[]): boolean {
  for (const k of keys) {
    const v = obj[k];
    if (v === true || v === false) return v;
    if (v === 'true' || v === 1) return true;
    if (v === 'false' || v === 0) return false;
  }
  return false;
}

function asArray(raw: unknown): unknown[] {
  return Array.isArray(raw) ? raw : [];
}

function apiHeaders(signal?: AbortSignal) {
  const applicationCode = workflowAppCode();
  return {
    headers: { applicationCode, 'application-code': applicationCode },
    signal,
  };
}

export type WorkflowTemplateStageRole = {
  id: string;
  roleId: number;
  roleName: string;
  workflowTemplateStageId: string;
  workflowTemplateStageName: string;
  sequenceNumber: number;
};

export type WorkflowTemplateStageRevertOption = {
  id: string;
  workflowTemplateId: string;
  sequenceNumber: number;
  name: string;
  defaultNextStage: boolean;
  revertAllowed: boolean;
};

export type EscalationEligibleUser = {
  userId: string;
  title: string;
  displayName: string;
  recipientName: string;
  roles: string;
};

export type WorkflowTemplateStageEscalationData = {
  workflowTemplateStageId: string;
  message: string;
  escalationRuleId: string | null;
  escalationUsers: EscalationEligibleUser[];
};

export type WorkflowTemplateStageKpi = {
  id: string;
  workflowTemplateStageId: string;
  ttc: number;
  ttcTimeUnit: string;
  ttr: number;
  ttrTimeUnit: string;
  tte: number;
  tteTimeUnit: string;
};

function normalizeStageRole(raw: unknown): WorkflowTemplateStageRole | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = strField(r, 'id');
  const role = r.role;
  const stage = r.workflow_template_stage ?? r.workflowTemplateStage;
  if (!id || !role || typeof role !== 'object' || !stage || typeof stage !== 'object') return null;
  const roleObj = role as Record<string, unknown>;
  const stageObj = stage as Record<string, unknown>;
  const roleId = numField(roleObj, 'id', 'role_id', 'roleId');
  const roleName = strField(roleObj, 'name', 'role_name', 'roleName');
  const workflowTemplateStageId = strField(stageObj, 'id', 'workflow_template_stage_id', 'workflowTemplateStageId');
  if (!roleId || !workflowTemplateStageId) return null;
  return {
    id,
    roleId,
    roleName,
    workflowTemplateStageId,
    workflowTemplateStageName: strField(stageObj, 'name', 'stage_name', 'stageName'),
    sequenceNumber: numField(stageObj, 'sequence_number', 'sequenceNumber'),
  };
}

function normalizeRevertOption(raw: unknown): WorkflowTemplateStageRevertOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const nested = r.workflow_template_stage ?? r.workflowTemplateStage;
  const nestedObj =
    nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : null;

  const id =
    strField(r, 'id', 'workflow_template_stage_id', 'workflowTemplateStageId') ||
    (nestedObj
      ? strField(nestedObj, 'id', 'workflow_template_stage_id', 'workflowTemplateStageId')
      : '');
  const name =
    strField(r, 'name', 'stage_name', 'stageName') ||
    (nestedObj ? strField(nestedObj, 'name', 'stage_name', 'stageName') : '');
  if (!id) return null;
  return {
    id,
    workflowTemplateId:
      strField(r, 'workflow_template_id', 'workflowTemplateId') ||
      (nestedObj ? strField(nestedObj, 'workflow_template_id', 'workflowTemplateId') : ''),
    sequenceNumber:
      numField(r, 'sequence_number', 'sequenceNumber') ||
      (nestedObj ? numField(nestedObj, 'sequence_number', 'sequenceNumber') : 0),
    name,
    defaultNextStage: boolField(r, 'defaultNextStage', 'default_next_stage'),
    revertAllowed: boolField(r, 'revertAllowed', 'revert_allowed'),
  };
}

function normalizeEligibleUser(raw: unknown): EscalationEligibleUser | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const userId = strField(r, 'userId', 'user_id', 'id');
  const displayName = strField(r, 'displayName', 'display_name', 'name');
  if (!userId || !displayName) return null;
  return {
    userId,
    title: strField(r, 'title'),
    displayName,
    recipientName: strField(r, 'recipientName', 'recipient_name'),
    roles: strField(r, 'roles', 'role_names', 'roleNames'),
  };
}

function normalizeStageKpi(raw: unknown): WorkflowTemplateStageKpi | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = strField(r, 'id');
  const workflowTemplateStageId = strField(r, 'workflowTemplateStageId', 'workflow_template_stage_id');
  if (!id || !workflowTemplateStageId) return null;
  return {
    id,
    workflowTemplateStageId,
    ttc: numField(r, 'ttc'),
    ttcTimeUnit: strField(r, 'ttcTimeUnit', 'ttc_time_unit'),
    ttr: numField(r, 'ttr'),
    ttrTimeUnit: strField(r, 'ttrTimeUnit', 'ttr_time_unit'),
    tte: numField(r, 'tte'),
    tteTimeUnit: strField(r, 'tteTimeUnit', 'tte_time_unit'),
  };
}

function normalizeEscalationData(raw: unknown): WorkflowTemplateStageEscalationData | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const workflowTemplateStageId = strField(r, 'workflowTemplateStageId', 'workflow_template_stage_id');
  if (!workflowTemplateStageId) return null;
  const rule = r.escalationRule ?? r.escalation_rule;
  let escalationRuleId: string | null = null;
  if (rule && typeof rule === 'object') {
    escalationRuleId = strField(rule as Record<string, unknown>, 'id') || null;
  }
  const usersRaw = r.escalationUsers ?? r.escalation_users;
  const escalationUsers = asArray(usersRaw)
    .map(normalizeEligibleUser)
    .filter((x): x is EscalationEligibleUser => Boolean(x));
  return {
    workflowTemplateStageId,
    message: strField(r, 'message'),
    escalationRuleId,
    escalationUsers,
  };
}

export function formatStageLabel(sequenceNumber: number, name: string): string {
  const trimmed = name.trim();
  if (sequenceNumber > 0 && trimmed) return `Stage ${sequenceNumber} - ${trimmed}`;
  if (sequenceNumber > 0) return `Stage ${sequenceNumber}`;
  return trimmed || 'Stage';
}

function canvasStageName(label: string): string {
  return label.replace(/^Stage \d+ [:-] /, '').trim();
}

/** Map locally stored revert keys (canvas ids) to API workflow template stage ids. */
export function mapRevertKeysToStageIds(
  keys: string[],
  apiOptions: WorkflowTemplateStageRevertOption[],
  canvasOptions: { id: string; label: string }[],
): string[] {
  const apiIds = new Set(apiOptions.map((o) => o.id));
  const mapped: string[] = [];

  for (const key of keys) {
    if (apiIds.has(key)) {
      mapped.push(key);
      continue;
    }

    const canvasOpt = canvasOptions.find((o) => o.id === key);
    if (!canvasOpt) continue;

    const byLabel = apiOptions.find(
      (o) => formatStageLabel(o.sequenceNumber, o.name) === canvasOpt.label,
    );
    if (byLabel) {
      mapped.push(byLabel.id);
      continue;
    }

    const canvasName = canvasStageName(canvasOpt.label);
    const byName = apiOptions.find((o) => o.name.trim() === canvasName);
    if (byName) mapped.push(byName.id);
  }

  return [...new Set(mapped)];
}

export function buildRevertStageOptions(
  apiOptions: WorkflowTemplateStageRevertOption[],
  canvasFallback: { id: string; label: string }[],
  selectedIds: string[],
): { id: string; label: string }[] {
  const revertCandidates = apiOptions.filter((o) => !o.defaultNextStage);
  const mapped =
    revertCandidates.length > 0
      ? revertCandidates.map((o) => ({
          id: o.id,
          label: formatStageLabel(o.sequenceNumber, o.name),
        }))
      : [...canvasFallback];

  const byId = new Map(mapped.map((o) => [o.id, o]));
  for (const id of selectedIds) {
    if (byId.has(id)) continue;
    const opt = apiOptions.find((o) => o.id === id);
    if (opt) {
      byId.set(opt.id, {
        id: opt.id,
        label: formatStageLabel(opt.sequenceNumber, opt.name),
      });
    }
  }

  return [...byId.values()];
}

export function resolveDefaultNextStageLabel(
  apiOptions: WorkflowTemplateStageRevertOption[],
  workflowTemplateStageId: string,
  fallback: string,
): string {
  const flagged = apiOptions.find((o) => o.defaultNextStage);
  if (flagged) return formatStageLabel(flagged.sequenceNumber, flagged.name);

  const current = apiOptions.find((o) => o.id === workflowTemplateStageId);
  if (current) {
    const next = apiOptions.find((o) => o.sequenceNumber === current.sequenceNumber + 1);
    if (next) return formatStageLabel(next.sequenceNumber, next.name);
  }

  return fallback;
}

/** CMS-WTSR-1 — GET workflow template stage roles */
export async function getWorkflowTemplateStageRoles(input: {
  workflowTemplateStageId: string;
  signal?: AbortSignal;
}): Promise<ApiResult<WorkflowTemplateStageRole[]>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  const qs = new URLSearchParams({ workflowTemplateStageId });
  const res = await caseApiRequest<unknown>(`/workflow-template-stage-roles?${qs.toString()}`, {
    method: 'GET',
    ...apiHeaders(input.signal),
  });
  if (!res.ok) return { success: false, message: res.message };
  const data = asArray(res.data)
    .map(normalizeStageRole)
    .filter((x): x is WorkflowTemplateStageRole => Boolean(x));
  return { success: true, data };
}

/** CMS-WTSR-2 — POST workflow template stage role */
export async function createWorkflowTemplateStageRole(input: {
  workflowTemplateStageId: string;
  roleId: number;
}): Promise<ApiResult<WorkflowTemplateStageRole>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  if (!input.roleId) return { success: false, message: 'User role is required.' };

  const res = await caseApiRequest<unknown>('/workflow-template-stage-roles', {
    method: 'POST',
    body: {
      workflow_template_stage_id: workflowTemplateStageId,
      role_id: input.roleId,
    },
    ...apiHeaders(),
  });
  if (!res.ok) return { success: false, message: res.message };
  const data = normalizeStageRole(res.data);
  if (!data) return { success: false, message: 'Stage role response is invalid.' };
  return { success: true, data };
}

/** CMS-WTSR-3 — PUT workflow template stage role */
export async function updateWorkflowTemplateStageRole(input: {
  id: string;
  workflowTemplateStageId: string;
  roleId: number;
}): Promise<ApiResult<WorkflowTemplateStageRole>> {
  const id = input.id.trim();
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(id)) return { success: false, message: 'Stage role id is required.' };
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  if (!input.roleId) return { success: false, message: 'User role is required.' };

  const res = await caseApiRequest<unknown>('/workflow-template-stage-roles', {
    method: 'PUT',
    body: {
      id,
      workflow_template_stage_id: workflowTemplateStageId,
      role_id: input.roleId,
    },
    ...apiHeaders(),
  });
  if (!res.ok) return { success: false, message: res.message };
  const data = normalizeStageRole(res.data);
  if (!data) return { success: false, message: 'Stage role response is invalid.' };
  return { success: true, data };
}

/** CMS-WTSRR-1 — GET revert stage options */
export async function getWorkflowTemplateStageRevertOptions(input: {
  workflowTemplateStageId: string;
  signal?: AbortSignal;
}): Promise<ApiResult<WorkflowTemplateStageRevertOption[]>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  const qs = new URLSearchParams({ workflowTemplateStageId });
  const res = await caseApiRequest<unknown>(
    `/workflow-template-stage-routing-rules/revert-options?${qs.toString()}`,
    { method: 'GET', ...apiHeaders(input.signal) },
  );
  if (!res.ok) return { success: false, message: res.message };
  const data = asArray(res.data)
    .map(normalizeRevertOption)
    .filter((x): x is WorkflowTemplateStageRevertOption => Boolean(x));
  return { success: true, data };
}

/** CMS-WTSRR-2 — POST routing rules */
export async function createWorkflowTemplateStageRoutingRules(input: {
  workflowTemplateStageId: string;
  revertWorkflowTemplateStageIds: string[];
}): Promise<ApiResult<void>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  const res = await caseApiRequest<unknown>('/workflow-template-stage-routing-rules', {
    method: 'POST',
    body: {
      workflow_template_stage_id: workflowTemplateStageId,
      revert_workflow_template_stage_ids: input.revertWorkflowTemplateStageIds,
    },
    ...apiHeaders(),
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** CMS-WTSRR-3 — PUT routing rules */
export async function updateWorkflowTemplateStageRoutingRules(input: {
  workflowTemplateStageId: string;
  revertWorkflowTemplateStageIds: string[];
}): Promise<ApiResult<void>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  const res = await caseApiRequest<unknown>('/workflow-template-stage-routing-rules', {
    method: 'PUT',
    body: {
      workflow_template_stage_id: workflowTemplateStageId,
      revert_workflow_template_stage_ids: input.revertWorkflowTemplateStageIds,
    },
    ...apiHeaders(),
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** CMS-WTSER-1 — GET escalation rules */
export async function getWorkflowTemplateStageEscalationRules(input: {
  workflowTemplateStageId: string;
  signal?: AbortSignal;
}): Promise<ApiResult<WorkflowTemplateStageEscalationData>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  const qs = new URLSearchParams({ workflowTemplateStageId });
  const res = await caseApiRequest<unknown>(
    `/workflow-template-stage-escalation-rules?${qs.toString()}`,
    { method: 'GET', ...apiHeaders(input.signal) },
  );
  if (!res.ok) return { success: false, message: res.message };
  const data = normalizeEscalationData(res.data);
  if (!data) return { success: false, message: 'Escalation rules response is invalid.' };
  return { success: true, data };
}

/** CMS-WTSER-4 — GET escalation eligible users */
export async function getEscalationEligibleUsers(input?: {
  signal?: AbortSignal;
}): Promise<ApiResult<EscalationEligibleUser[]>> {
  const res = await caseApiRequest<unknown>('/workflow-template-stage-escalation-rules/eligible-users', {
    method: 'GET',
    ...apiHeaders(input?.signal),
  });
  if (!res.ok) return { success: false, message: res.message };
  const data = asArray(res.data)
    .map(normalizeEligibleUser)
    .filter((x): x is EscalationEligibleUser => Boolean(x));
  return { success: true, data };
}

/** CMS-WTSER-2 — POST escalation rule users */
export async function createEscalationRuleUsers(input: {
  workflowTemplateStageId: string;
  userIds: string[];
}): Promise<ApiResult<void>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  const res = await caseApiRequest<unknown>('/workflow-template-stage-escalation-rules/users', {
    method: 'POST',
    body: {
      workflowTemplateStageId,
      userIds: input.userIds,
    },
    ...apiHeaders(),
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** CMS-WTSER-3 — PUT escalation rule users */
export async function updateEscalationRuleUsers(input: {
  workflowTemplateStageId: string;
  userIds: string[];
}): Promise<ApiResult<void>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  const res = await caseApiRequest<unknown>('/workflow-template-stage-escalation-rules/users', {
    method: 'PUT',
    body: {
      workflowTemplateStageId,
      userIds: input.userIds,
    },
    ...apiHeaders(),
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** CMS-WTSK-2 — GET workflow template stage KPI by stage id */
export async function getWorkflowTemplateStageKpi(input: {
  workflowTemplateStageId: string;
  signal?: AbortSignal;
}): Promise<ApiResult<WorkflowTemplateStageKpi | null>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  const res = await workflowApiRequest<unknown>(
    `/workflow-template-stages-kpi/${encodeURIComponent(workflowTemplateStageId)}`,
    { method: 'GET', ...apiHeaders(input.signal) },
  );
  if (!res.ok) {
    if (res.status === 404) return { success: true, data: null };
    return { success: false, message: res.message };
  }
  const data = normalizeStageKpi(res.data);
  if (!data) return { success: false, message: 'Stage KPI response is invalid.' };
  return { success: true, data };
}

/** CMS-WTSK-1 — POST create workflow template stage KPI */
export async function createWorkflowTemplateStageKpi(input: {
  workflowTemplateStageId: string;
  ttc: number;
  ttcTimeUnitId: string;
  ttr: number;
  ttrTimeUnitId: string;
  tte: number;
  tteTimeUnitId: string;
}): Promise<ApiResult<WorkflowTemplateStageKpi>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  if (!input.ttcTimeUnitId || !input.ttrTimeUnitId || !input.tteTimeUnitId) {
    return { success: false, message: 'Time unit is required for all KPI fields.' };
  }

  const res = await workflowApiRequest<unknown>('/workflow-template-stages-kpi/create', {
    method: 'POST',
    body: {
      workflow_template_stage_id: workflowTemplateStageId,
      ttc: input.ttc,
      ttc_time_unit_id: input.ttcTimeUnitId,
      ttr: input.ttr,
      ttr_time_unit_id: input.ttrTimeUnitId,
      tte: input.tte,
      tte_time_unit_id: input.tteTimeUnitId,
    },
    ...apiHeaders(),
  });
  if (!res.ok) return { success: false, message: res.message };
  const data = normalizeStageKpi(res.data);
  if (!data) return { success: false, message: 'Stage KPI response is invalid.' };
  return { success: true, data };
}

/** CMS-WTSK-3 — PUT update workflow template stage KPI */
export async function updateWorkflowTemplateStageKpi(input: {
  workflowTemplateStageId: string;
  ttc: number;
  ttcTimeUnitId: string;
  ttr: number;
  ttrTimeUnitId: string;
  tte: number;
  tteTimeUnitId: string;
}): Promise<ApiResult<WorkflowTemplateStageKpi>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Workflow template stage id is required.' };
  }
  if (!input.ttcTimeUnitId || !input.ttrTimeUnitId || !input.tteTimeUnitId) {
    return { success: false, message: 'Time unit is required for all KPI fields.' };
  }

  const res = await workflowApiRequest<unknown>(
    `/workflow-template-stages-kpi/${encodeURIComponent(workflowTemplateStageId)}`,
    {
      method: 'PUT',
      body: {
        workflow_template_stage_id: workflowTemplateStageId,
        ttc: input.ttc,
        ttc_time_unit_id: input.ttcTimeUnitId,
        ttr: input.ttr,
        ttr_time_unit_id: input.ttrTimeUnitId,
        tte: input.tte,
        tte_time_unit_id: input.tteTimeUnitId,
      },
      ...apiHeaders(),
    },
  );
  if (!res.ok) return { success: false, message: res.message };
  const data = normalizeStageKpi(res.data);
  if (!data) return { success: false, message: 'Stage KPI response is invalid.' };
  return { success: true, data };
}

export function resolveWorkflowTemplateStageId(stage: {
  id: string;
  backendStageId?: string;
}): string | undefined {
  const candidate = (stage.backendStageId ?? stage.id).trim();
  return isCaseRequestUuid(candidate) ? candidate : undefined;
}
